// src/components/ArticleCompatibilitySection.tsx
// Manages compatible articles for a given article (search, add, remove, bulk)

import { supabase } from '@/lib/supabase';
import type { Article, CompatibleArticleDetail } from '@/types';
import { logCompatibilityAdded, logCompatibilityRemoved } from '@/utils/activityLogger';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link2, Plus, Search, Trash2, X } from 'lucide-react';
import { useState } from 'react';

interface ArticleCompatibilitySectionProps {
    articleId: string;
    articleCode: string;
    customerUuid: string;
}

export default function ArticleCompatibilitySection({
    articleId,
    articleCode,
    customerUuid
}: ArticleCompatibilitySectionProps) {
    const queryClient = useQueryClient();
    const [searchQuery, setSearchQuery] = useState('');
    const [newNote, setNewNote] = useState('');
    const [showSearch, setShowSearch] = useState(false);
    const [bulkMode, setBulkMode] = useState(false);
    const [bulkSelected, setBulkSelected] = useState<string[]>([]);
    const [bulkNote, setBulkNote] = useState('');

    // Fetch current compatibilities for this article
    const { data: compatibilities = [], isLoading } = useQuery<CompatibleArticleDetail[]>({
        queryKey: ['article_compatibilities', articleId],
        queryFn: async () => {
            const { data, error } = await supabase
                .from('article_compatibilities')
                .select(`
          id,
          note,
          compatible_article_id,
          compatible_article:articles!article_compatibilities_compatible_article_id_fkey(
            id,
            codice_prodotto,
            descrizione_prodotto,
            quantita_magazzino_attuale,
            quantita_minima_magazzino
          )
        `)
                .eq('article_id', articleId)
                .eq('customer_uuid', customerUuid);

            if (error) throw error;

            return (data || []).map((row: any) => ({
                id: row.compatible_article?.id || row.compatible_article_id,
                compatibility_id: row.id,
                article_id: row.compatible_article_id,
                codice_prodotto: row.compatible_article?.codice_prodotto || '—',
                descrizione_prodotto: row.compatible_article?.descrizione_prodotto || null,
                quantita_magazzino_attuale: row.compatible_article?.quantita_magazzino_attuale || 0,
                quantita_minima_magazzino: row.compatible_article?.quantita_minima_magazzino || 0,
                note: row.note || ''
            }));
        },
        enabled: !!articleId && !!customerUuid
    });

    // Search for articles to add
    const { data: searchResults = [] } = useQuery<Article[]>({
        queryKey: ['articles_search_compat', searchQuery, articleId],
        queryFn: async () => {
            if (!searchQuery || searchQuery.length < 2) return [];

            const { data, error } = await supabase
                .from('articles')
                .select('*')
                .eq('customer_uuid', customerUuid)
                .neq('id', articleId)
                .or(`codice_prodotto.ilike.%${searchQuery}%,descrizione_prodotto.ilike.%${searchQuery}%`)
                .order('codice_prodotto')
                .limit(10);

            if (error) throw error;

            // Exclude already linked articles
            const linkedIds = new Set(compatibilities.map(c => c.article_id));
            return (data || []).filter((a: Article) => !linkedIds.has(a.id)) as Article[];
        },
        enabled: searchQuery.length >= 2
    });

    // Add single compatibility
    const addCompatibility = useMutation({
        mutationFn: async ({ compatibleArticleId, note }: { compatibleArticleId: string; note: string }) => {
            const { error } = await supabase.rpc('add_article_compatibility', {
                p_customer_uuid: customerUuid,
                p_article_id: articleId,
                p_compatible_article_id: compatibleArticleId,
                p_note: note
            });
            if (error) throw error;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['article_compatibilities'] });
            queryClient.invalidateQueries({ queryKey: ['articles_compat_counts'] });
            const matched = searchResults.find(a => a.id === variables.compatibleArticleId);
            if (matched) {
                logCompatibilityAdded(articleId, articleCode, matched.codice_prodotto);
            }
            setSearchQuery('');
            setNewNote('');
            setShowSearch(false);
        },
        onError: (error: any) => {
            alert(`Errore: ${error.message}`);
        }
    });

    // Remove compatibility
    const removeCompatibility = useMutation({
        mutationFn: async ({ compatibleArticleId }: { compatibleArticleId: string }) => {
            const { error } = await supabase.rpc('remove_article_compatibility', {
                p_customer_uuid: customerUuid,
                p_article_id: articleId,
                p_compatible_article_id: compatibleArticleId
            });
            if (error) throw error;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: ['article_compatibilities'] });
            queryClient.invalidateQueries({ queryKey: ['articles_compat_counts'] });
            const removed = compatibilities.find(c => c.article_id === variables.compatibleArticleId);
            if (removed) {
                logCompatibilityRemoved(articleId, articleCode, removed.codice_prodotto);
            }
        },
        onError: (error: any) => {
            alert(`Errore: ${error.message}`);
        }
    });

    // Bulk add
    const bulkAddCompatibility = useMutation({
        mutationFn: async ({ articleIds, note }: { articleIds: string[]; note: string }) => {
            // Include the current article in the group
            const allIds = [articleId, ...articleIds];
            const { error } = await supabase.rpc('add_bulk_compatibility', {
                p_customer_uuid: customerUuid,
                p_article_ids: allIds,
                p_note: note
            });
            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['article_compatibilities'] });
            queryClient.invalidateQueries({ queryKey: ['articles_compat_counts'] });
            setBulkMode(false);
            setBulkSelected([]);
            setBulkNote('');
            setSearchQuery('');
        },
        onError: (error: any) => {
            alert(`Errore: ${error.message}`);
        }
    });

    const toggleBulkItem = (id: string) => {
        setBulkSelected(prev =>
            prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
        );
    };

    const isLowStock = (qty: number, min: number) => qty <= min;

    return (
        <div className="bg-white rounded-lg border border-slate-200 p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Link2 className="w-5 h-5 text-blue-600" />
                    <h3 className="text-sm font-semibold text-slate-800">
                        Articoli Compatibili
                        {compatibilities.length > 0 && (
                            <span className="ml-2 text-xs font-normal text-slate-500">
                                ({compatibilities.length})
                            </span>
                        )}
                    </h3>
                </div>
                <div className="flex gap-2">
                    {!bulkMode && (
                        <button
                            onClick={() => setBulkMode(true)}
                            className="text-xs text-slate-500 hover:text-blue-600 transition-colors"
                        >
                            Gruppo
                        </button>
                    )}
                    <button
                        onClick={() => {
                            setShowSearch(!showSearch);
                            setBulkMode(false);
                            setBulkSelected([]);
                            setSearchQuery('');
                        }}
                        className="p-1.5 hover:bg-blue-100 text-blue-600 rounded-lg transition-colors"
                        title="Aggiungi compatibilità"
                    >
                        <Plus className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Current compatibilities list */}
            {isLoading ? (
                <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
            ) : compatibilities.length === 0 && !showSearch && !bulkMode ? (
                <p className="text-sm text-slate-400 text-center py-3">
                    Nessun articolo compatibile configurato
                </p>
            ) : (
                <div className="space-y-2 mb-3">
                    {compatibilities.map(comp => (
                        <div
                            key={comp.compatibility_id}
                            className={`flex items-center justify-between p-2 rounded-lg border ${isLowStock(comp.quantita_magazzino_attuale, comp.quantita_minima_magazzino)
                                ? 'border-orange-200 bg-orange-50'
                                : 'border-slate-100 bg-slate-50'
                                }`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-sm font-semibold text-slate-800 truncate">
                                        {comp.codice_prodotto}
                                    </span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${isLowStock(comp.quantita_magazzino_attuale, comp.quantita_minima_magazzino)
                                        ? 'bg-orange-200 text-orange-800'
                                        : 'bg-green-100 text-green-800'
                                        }`}>
                                        Qty: {comp.quantita_magazzino_attuale}
                                    </span>
                                </div>
                                {comp.descrizione_prodotto && (
                                    <p className="text-xs text-slate-500 truncate">{comp.descrizione_prodotto}</p>
                                )}
                                {comp.note && (
                                    <p className="text-xs text-blue-500 mt-0.5 italic">{comp.note}</p>
                                )}
                            </div>
                            <button
                                onClick={() => removeCompatibility.mutate({ compatibleArticleId: comp.article_id })}
                                className="p-1.5 hover:bg-red-100 text-red-500 rounded transition-colors flex-shrink-0 ml-2"
                                title="Rimuovi compatibilità"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Search / Add single */}
            {showSearch && !bulkMode && (
                <div className="border-t border-slate-200 pt-3 mt-2 space-y-2">
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Cerca articolo per codice o descrizione..."
                            className="w-full pl-8 pr-8 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                        />
                        {searchQuery && (
                            <button
                                onClick={() => setSearchQuery('')}
                                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 hover:bg-slate-200 rounded"
                            >
                                <X className="w-3.5 h-3.5 text-slate-400" />
                            </button>
                        )}
                    </div>
                    <input
                        type="text"
                        value={newNote}
                        onChange={e => setNewNote(e.target.value)}
                        placeholder="Nota compatibilità (opzionale)..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {searchQuery.length >= 2 && (
                        <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                            {searchResults.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-3">Nessun risultato</p>
                            ) : (
                                searchResults.map(art => (
                                    <button
                                        key={art.id}
                                        onClick={() => addCompatibility.mutate({ compatibleArticleId: art.id, note: newNote })}
                                        className="w-full flex items-center justify-between px-3 py-2 hover:bg-blue-50 transition-colors text-left border-b border-slate-100 last:border-b-0"
                                        disabled={addCompatibility.isPending}
                                    >
                                        <div className="min-w-0 flex-1">
                                            <span className="text-sm font-medium text-slate-800">{art.codice_prodotto}</span>
                                            {art.descrizione_prodotto && (
                                                <p className="text-xs text-slate-500 truncate">{art.descrizione_prodotto}</p>
                                            )}
                                        </div>
                                        <span className="text-xs text-slate-500 ml-2 flex-shrink-0">
                                            Qty: {art.quantita_magazzino_attuale}
                                        </span>
                                    </button>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Bulk mode */}
            {bulkMode && (
                <div className="border-t border-slate-200 pt-3 mt-2 space-y-2">
                    <p className="text-xs text-slate-500">
                        Seleziona più articoli da rendere tutti compatibili tra loro:
                    </p>
                    <div className="relative">
                        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                            placeholder="Cerca articoli..."
                            className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                        />
                    </div>
                    <input
                        type="text"
                        value={bulkNote}
                        onChange={e => setBulkNote(e.target.value)}
                        placeholder="Nota per il gruppo (opzionale)..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {searchQuery.length >= 2 && (
                        <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg">
                            {searchResults.length === 0 ? (
                                <p className="text-sm text-slate-400 text-center py-3">Nessun risultato</p>
                            ) : (
                                searchResults.map(art => {
                                    const isSelected = bulkSelected.includes(art.id);
                                    return (
                                        <button
                                            key={art.id}
                                            onClick={() => toggleBulkItem(art.id)}
                                            className={`w-full flex items-center justify-between px-3 py-2 transition-colors text-left border-b border-slate-100 last:border-b-0 ${isSelected ? 'bg-blue-50' : 'hover:bg-slate-50'
                                                }`}
                                        >
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'
                                                    }`}>
                                                    {isSelected && <span className="text-white text-xs">✓</span>}
                                                </div>
                                                <div className="min-w-0">
                                                    <span className="text-sm font-medium text-slate-800">{art.codice_prodotto}</span>
                                                    {art.descrizione_prodotto && (
                                                        <p className="text-xs text-slate-500 truncate">{art.descrizione_prodotto}</p>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-xs text-slate-500 ml-2 flex-shrink-0">
                                                Qty: {art.quantita_magazzino_attuale}
                                            </span>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    )}
                    {/* Bulk selected items */}
                    {bulkSelected.length > 0 && (
                        <div className="flex flex-wrap gap-1.5">
                            {bulkSelected.map(id => {
                                const art = searchResults.find(a => a.id === id);
                                return art ? (
                                    <span
                                        key={id}
                                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs"
                                    >
                                        {art.codice_prodotto}
                                        <button onClick={() => toggleBulkItem(id)} className="hover:text-red-600">
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                ) : null;
                            })}
                        </div>
                    )}
                    <div className="flex gap-2">
                        <button
                            onClick={() => {
                                setBulkMode(false);
                                setBulkSelected([]);
                                setBulkNote('');
                                setSearchQuery('');
                            }}
                            className="flex-1 px-3 py-2 bg-slate-200 hover:bg-slate-300 text-slate-700 rounded-lg text-sm transition-colors"
                        >
                            Annulla
                        </button>
                        <button
                            onClick={() => bulkAddCompatibility.mutate({ articleIds: bulkSelected, note: bulkNote })}
                            disabled={bulkSelected.length < 1 || bulkAddCompatibility.isPending}
                            className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded-lg text-sm transition-colors"
                        >
                            {bulkAddCompatibility.isPending ? 'Salvataggio...' : `Collega ${bulkSelected.length} articoli`}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
